import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { createBMI, updateBMI } from '../../../../store/participantService';
import { todayDate } from '../../../../utils/dateFunctions';
import { debounce } from '../../../../utils/debounce';
import { getObjFromId } from '../../../../utils/directusFunctions';
import { formatFloat } from '../../../../utils/numberUtils';
import DateSelect from '../../../formElements/DateSelect';
import SingleSelect from '../../../formElements/SingleSelect';
import Switch from '../../../formElements/Switch';
import TextInputField from '../../../formElements/TextInputField';
import FormActions from '../../../ui/formActions/FormActions';
import WarningMessage from '../../../ui/messageBanners/WarningMessage';

import {
    checkHeightWarnings,
    checkWeightWarnings,
    MAX_INS_HEIGHT,
    MAX_LBS_WEIGHT,
    validate
} from './validateBMIEntry';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const _WARNING_INTERVAL = 500;

const EditBMIEntry = ({
    participant,
    onClose,
    isDisabled,
    attendedModules,
    linkedModuleId,
    formType,
    row = {},
    lastRow
}) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const _MESSAGE = `Participant BMI entry has been ${formType === 'create' ? 'added' : 'updated'}`;
    const initialState = {
        id: '',
        participantId: '',
        height: '',
        weight: '',
        eventDate: todayDate(),
        measurementId: '',
        eventId: '',
        linkedModuleId: ''
    };
    const initialImperialHeight = { ft: '', ins: '' };
    const initialImperialWeight = { st: '', lbs: '' };
    const initialConfirmWarning = { height: false, weight: false };

    const [newEntry, setNewEntry] = useState(initialState);
    const [initialRow, setInitialRow] = useState({});
    const [modules, setModules] = useState([]);

    const [isImperialHeight, setIsImperialHeight] = useState(false);
    const [imperialHeight, setImperialHeight] = useState(initialImperialHeight);

    const [isImperialWeight, setIsImperialWeight] = useState(false);
    const [imperialWeight, setImperialWeight] = useState(initialImperialWeight);

    const [errors, setErrors] = useState({});
    const [warnings, setWarnings] = useState({});
    const [confirmWarning, setConfirmWarning] = useState(initialConfirmWarning);

    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const measurementTypes = useSelector(
        (state) => state.entities.directusService.measurementTypes
    );
    const bloodTestEvents = useSelector((state) => state.entities.directusService.bloodTestEvents);

    // USE EFFECTS
    useEffect(() => {
        return () => configWarning.current.cancel?.();
    }, []);

    useEffect(() => {
        if (Object.keys(row).length < 1 || formType !== 'edit') {
            setNewEntry({
                ...initialState,
                height: lastRow?.height || '',
                linkedModuleId: linkedModuleId || ''
            });
        } else {
            const updatedRow = {
                id: row.id,
                participantId: row.participantId,
                eventDate: row.eventDate,
                eventId: row.eventId,
                height: row.height,
                weight: row.weight,
                linkedModuleId: row.linkedModuleId,
                measurementId: row.measurementId
            };
            setNewEntry(updatedRow);
            setInitialRow(updatedRow);
        }
    }, [row, lastRow]);

    useEffect(() => {
        let modules = [...attendedModules];
        if (formType === 'edit') {
            if (
                row.linkedModuleId &&
                row.linkedModule &&
                !modules.find((el) => el.id === row.linkedModuleId)
            ) {
                modules = [...modules, { id: row.linkedModuleId, name: row.linkedModule }];
            }
        }
        setModules(modules);
    }, [attendedModules]);

    useEffect(() => {
        let isDisabled = false;
        if (Object.values(errors).some((el) => el.error)) isDisabled = true;
        if (warnings?.height?.warning && !confirmWarning.height) isDisabled = true;
        if (warnings?.weight?.warning && !confirmWarning.weight) isDisabled = true;
        setIsButtonDisabled(isDisabled);
    }, [errors, warnings, confirmWarning]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...prev[key], error: false } }));
    };

    const clearWarning = (key) => {
        if (key in warnings && warnings[key].warning)
            setWarnings((prev) => ({ ...prev, [key]: { ...prev[key], warning: false } }));
    };

    const convertHeight = (ft, ins) => Math.round((+ft * 30.48 + +ins * 2.54) * 100) / 100;

    const convertWeight = (st, lbs) => Math.round((+st * 6.35029 + +lbs / 14) * 100) / 100;

    const statsConfig = (key, value) => {
        clearWarning(key);
        onConfirmWarning(key, false);
        return formatFloat(value);
    };

    const configWarning = useRef(
        debounce(
            (value, isImperial, checkFunction, warnings) =>
                setWarnings(checkFunction(value, warnings, isImperial)),
            _WARNING_INTERVAL
        )
    );

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onConfirmWarning = (key, value) => {
        setConfirmWarning((prev) => ({ ...prev, [key]: value }));
    };

    const onSwitchHeightUnits = (e) => {
        clearWarning('height');
        setIsImperialHeight(e.target.checked);
        setImperialHeight(initialImperialHeight);
        onConfirmWarning('height', false);
        onUpdate('height', '');
    };

    const onSwitchWeightUnits = (e) => {
        clearWarning('weight');
        setIsImperialWeight(e.target.checked);
        setImperialWeight(initialImperialWeight);
        onConfirmWarning('weight', false);
        onUpdate('weight', '');
    };

    const onHeightChange = (e) => {
        const value = statsConfig('height', e.target.value);
        onUpdate('height', value);
        configWarning.current(value, isImperialHeight, checkHeightWarnings, warnings);
    };

    const onFeetChange = (e) => {
        clearError('height');
        const ft = statsConfig('height', e.target.value);
        const convertedValue = convertHeight(ft, imperialHeight.ins);
        setImperialHeight((prev) => ({ ...prev, ft }));
        configWarning.current(convertedValue, isImperialHeight, checkHeightWarnings, warnings);
    };

    const onInchChange = (e) => {
        const ins = statsConfig('height', e.target.value);
        const convertedValue = convertHeight(imperialHeight.ft, ins);
        setImperialHeight((prev) => ({
            ...prev,
            ins: ins > MAX_INS_HEIGHT ? MAX_INS_HEIGHT : ins
        }));
        configWarning.current(convertedValue, isImperialHeight, checkHeightWarnings, warnings);
    };

    const onWeightChange = (e) => {
        const value = statsConfig('weight', e.target.value);
        onUpdate('weight', value);
        configWarning.current(value, isImperialWeight, checkWeightWarnings, warnings);
    };

    const onStoneChange = (e) => {
        clearError('weight');
        const st = statsConfig('weight', e.target.value);
        const convertedValue = convertWeight(st, imperialWeight.lbs);
        setImperialWeight((prev) => ({ ...prev, st }));
        configWarning.current(convertedValue, isImperialWeight, checkWeightWarnings, warnings);
    };

    const onLbsChange = (e) => {
        const lbs = statsConfig('weight', e.target.value);
        const convertedValue = convertWeight(imperialWeight.st, lbs);
        setImperialWeight((prev) => ({
            ...prev,
            lbs: lbs > MAX_LBS_WEIGHT ? MAX_LBS_WEIGHT : lbs
        }));
        configWarning.current(convertedValue, isImperialWeight, checkWeightWarnings, warnings);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);

        let payload = { ...newEntry };
        if (imperialHeight.ft) {
            const height = convertHeight(imperialHeight.ft, imperialHeight.ins);
            payload = { ...payload, height };
        }
        if (imperialWeight.st) {
            const weight = convertWeight(imperialWeight.st, imperialWeight.lbs);
            payload = { ...payload, weight };
        }

        payload = { ...payload, participantId: participant?.id };

        const newErrors = validate(payload, formType, initialRow);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        formType === 'create'
            ? dispatch(createBMI(payload, _MESSAGE))
            : dispatch(updateBMI(payload.id, payload, _MESSAGE));
        onClose();
    };

    // RENDER
    return (
        <div className={`${form.formWrapper} ${app.fadein}`}>
            <form className={form.form} onSubmit={onSubmit} data-testid="edit_bmi_form">
                <h2 className={app.sectionHeading}>
                    {formType === 'create' ? 'Add a New BMI Value' : 'Edit BMI'}
                </h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <DateSelect
                            value={newEntry.eventDate}
                            label="Event Date"
                            disabled={isDisabled}
                            mandatory={true}
                            error={errors.eventDate}
                            onDateChange={(date) => onUpdate('eventDate', date)}
                        />

                        <Switch
                            id="isImperialHeight"
                            label="Height"
                            leftLabel="cm"
                            rightLabel="ft & in"
                            disabled={isDisabled}
                            checked={isImperialHeight}
                            onChange={onSwitchHeightUnits}
                        />

                        <div className={form.formSet}>
                            {isImperialHeight ? (
                                <div className={form.formColumnSplit}>
                                    <div className={form.colSplitSection}>
                                        <TextInputField
                                            id="heightFt"
                                            label="Height (ft)"
                                            placeholder="Enter feet value..."
                                            disabled={isDisabled}
                                            mandatory={true}
                                            maxLength={1}
                                            value={imperialHeight.ft}
                                            error={errors.height}
                                            onChange={onFeetChange}
                                        />
                                    </div>

                                    <div className={form.colSplitSection}>
                                        <TextInputField
                                            id="heightIn"
                                            label="Height (in)"
                                            placeholder="Enter inches value..."
                                            disabled={isDisabled}
                                            maxLength={2}
                                            value={imperialHeight.ins}
                                            onChange={onInchChange}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <TextInputField
                                    id="height"
                                    label="Height (cm)"
                                    placeholder="Enter height..."
                                    disabled={isDisabled}
                                    mandatory={true}
                                    maxLength={6}
                                    value={newEntry.height}
                                    error={errors.height}
                                    onChange={onHeightChange}
                                />
                            )}
                            {warnings?.height?.warning && !errors?.height?.error && (
                                <WarningMessage
                                    id="height"
                                    warning={warnings.height}
                                    check={confirmWarning.height}
                                    onCheck={onConfirmWarning}
                                />
                            )}
                        </div>
                        <SingleSelect
                            id="measurementId"
                            label="Measurement"
                            placeholder="Select measurement type..."
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={measurementTypes || []}
                            selectedId={newEntry.measurementId}
                            selected={getObjFromId(measurementTypes, newEntry.measurementId)}
                            error={errors.measurementId}
                            onChange={(chosenId) => onUpdate('measurementId', chosenId)}
                        />
                    </div>

                    <div className={form.formColumn}>
                        <SingleSelect
                            id="eventId"
                            label="Event"
                            placeholder="Select event..."
                            disabled={isDisabled}
                            menuItems={bloodTestEvents || []}
                            selectedId={newEntry.eventId || ''}
                            selected={getObjFromId(bloodTestEvents, newEntry.eventId)}
                            error={errors.eventId}
                            onChange={(chosenId) => onUpdate('eventId', chosenId)}
                        />

                        <Switch
                            id="isImperialWeight"
                            label="Weight"
                            leftLabel="kg"
                            rightLabel="st & lbs"
                            disabled={isDisabled}
                            checked={isImperialWeight}
                            onChange={onSwitchWeightUnits}
                        />

                        <div className={form.formSet}>
                            {isImperialWeight ? (
                                <div className={form.formColumnSplit}>
                                    <div className={form.colSplitSection}>
                                        <TextInputField
                                            id="weightSt"
                                            label="Weight (st)"
                                            placeholder="Enter stone value..."
                                            disabled={isDisabled}
                                            mandatory={true}
                                            maxLength={2}
                                            value={imperialWeight.st}
                                            error={errors.weight}
                                            onChange={onStoneChange}
                                        />
                                    </div>
                                    <div className={form.colSplitSection}>
                                        <TextInputField
                                            id="weightLb"
                                            label="Weight (lb)"
                                            placeholder="Enter pounds value..."
                                            disabled={isDisabled}
                                            maxLength={2}
                                            value={imperialWeight.lbs}
                                            onChange={onLbsChange}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <TextInputField
                                    id="weight"
                                    label="Weight (kg)"
                                    placeholder="Enter weight..."
                                    mandatory={true}
                                    disabled={isDisabled}
                                    maxLength={6}
                                    value={newEntry.weight}
                                    error={errors.weight}
                                    onChange={onWeightChange}
                                />
                            )}
                            {warnings?.weight?.warning && !errors?.weight?.error && (
                                <WarningMessage
                                    id="weight"
                                    warning={warnings.weight}
                                    check={confirmWarning.weight}
                                    onCheck={onConfirmWarning}
                                />
                            )}
                        </div>
                        <SingleSelect
                            id="linkedModuleId"
                            label="Linked Module"
                            placeholder="Select linked module..."
                            disabled={isDisabled}
                            menuItems={modules || []}
                            selectedId={newEntry.linkedModuleId}
                            selected={getObjFromId(modules, newEntry.linkedModuleId)}
                            onChange={(chosenId) => onUpdate('linkedModuleId', chosenId)}
                        />
                    </div>
                </div>

                <FormActions
                    onClose={onSubmit}
                    onCancel={onClose}
                    disabled={isButtonDisabled || isDisabled}
                    btnText={formType === 'create' ? 'Save' : 'Update'}
                    customClass="noTopBorder"
                    error={errors?.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

EditBMIEntry.propTypes = {
    participant: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    formType: PropTypes.string,
    row: PropTypes.object,
    attendedModules: PropTypes.arrayOf(PropTypes.object),
    linkedModuleId: PropTypes.string,
    lastRow: PropTypes.object
};

export default EditBMIEntry;
